// Business dates as ISO strings `YYYY-MM-DD` (CLAUDE.md rule 6). Arithmetic runs on UTC
// midnight, so daylight saving time never shifts a date.

const berlinDate = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Today's business date in Europe/Berlin as `YYYY-MM-DD`. */
export function berlinToday(now: Date): string {
  return berlinDate.format(now);
}

const DAY_MS = 86_400_000;

function toUtc(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

function fromUtc(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** ISO date from year, month (1–12) and day. */
export function isoDate(year: number, month: number, day: number): string {
  return fromUtc(new Date(Date.UTC(year, month - 1, day)));
}

export function addDays(date: string, days: number): string {
  const d = toUtc(date);
  d.setUTCDate(d.getUTCDate() + days);
  return fromUtc(d);
}

/** Number of calendar days from `from` to `to` (negative if `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / DAY_MS);
}

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export function isoWeekday(date: string): number {
  const day = toUtc(date).getUTCDay(); // 0 = Sunday
  return day === 0 ? 7 : day;
}

export function yearOf(date: string): number {
  return Number(date.slice(0, 4));
}

/** All dates from `from` to `to`, both inclusive; empty if `to` is before `from`. */
export function eachDay(from: string, to: string): string[] {
  const days: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) days.push(d);
  return days;
}

/** Monday and Sunday of the week containing `date`. */
export function weekOf(date: string): { from: string; to: string } {
  const from = addDays(date, 1 - isoWeekday(date));
  return { from, to: addDays(from, 6) };
}

/** First and last day of the month containing `date`. */
export function monthOf(date: string): { from: string; to: string } {
  const d = toUtc(date);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  return {
    from: fromUtc(new Date(Date.UTC(year, month, 1))),
    to: fromUtc(new Date(Date.UTC(year, month + 1, 0))),
  };
}

/** ISO 8601 week number (weeks start on Monday; week 1 contains the first Thursday). */
export function isoWeekNumber(date: string): number {
  const thursday = addDays(date, 4 - isoWeekday(date));
  const yearStart = `${thursday.slice(0, 4)}-01-01`;
  return Math.floor(daysBetween(yearStart, thursday) / 7) + 1;
}
